import React, { useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Dropdown, Icon, Typography } from '@getsynapse/design-system';
import PageTitle from 'Molecules/PageTitle/PageTitle';
import { FormOption } from 'utils/customTypes';
import { useDashboardData } from './hooks/useDashboardData';
import { PATHS } from 'utils/constants';
import DashboardsContentView from './components/DashboardsContentView';
import { DashboardContext } from './DashboardContext';
import { DashboardSaveControls } from './DashboardSaveControls';
import { dashboardSelectors } from 'state/normalized/dashboard/dashboardSlice';
import { RootState } from 'state/store';

const DashboardDetailPage: React.FC = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const { thisDashboard, allDashboards } = useDashboardData(dashboardId);
  const { canEdit } = useSelector((state: RootState) =>
    dashboardSelectors.selectDashboardAffordanceFlags(state, dashboardId)
  );

  const history = useHistory();
  const switchToDashboard = useCallback(
    async (id: string) => {
      history.push(`${PATHS.DASHBOARD_PAGE}/${id}`);
    },
    [history]
  );

  const allDashboardOptions: FormOption[] = useMemo(() => {
    return allDashboards
      .sort(({ id: aId }, { id: bId }) => {
        if (aId === thisDashboard?.id) return -1;
        if (bId === thisDashboard?.id) return 1;
        return 0;
      })
      .map(({ id, name }) => ({
        value: id,
        label: name,
      }));
  }, [allDashboards, thisDashboard]);

  const defaultOption: FormOption[] = useMemo(() => {
    return thisDashboard
      ? [
          {
            value: thisDashboard.id,
            label: thisDashboard.name,
          },
        ]
      : [];
  }, [thisDashboard]);

  return (
    <div className='h-full flex flex-col'>
      <DashboardContext.Provider value={{ dashboardId }}>
        <PageTitle
          titleComponent={
            <Dropdown
              popperDiffWidth
              options={allDashboardOptions}
              values={defaultOption}
              onChange={async ({ value }: FormOption) => {
                if (value === thisDashboard?.id) return;
                await switchToDashboard(value);
              }}
              listProps={{
                className: 'p-0 min-w-60 font-normal',
              }}
              renderTrigger={({ onToggleDropdown }) => {
                return (
                  <div className='flex pl-1'>
                    <Typography
                      variant='h4'
                      className='flex items-center gap-x-2 font-body text-h4-sm lg:text-h4 font-semibold text-neutral-black'
                    >
                      <div>{thisDashboard?.name || ''}</div>
                      <Button
                        variant='tertiary'
                        className='p-0 py-0 text-2xl'
                        onClick={onToggleDropdown}
                      >
                        <Icon name='chevron-down-outline' />
                      </Button>
                    </Typography>
                  </div>
                );
              }}
            />
          }
          headerChildren={canEdit ? <DashboardSaveControls /> : undefined}
        />
        <DashboardsContentView />
      </DashboardContext.Provider>
    </div>
  );
};

export default DashboardDetailPage;
